import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { InputWithAlert } from '@/components/ui/input-with-alert';
import { DatePicker } from '@/components/ui/date-picker';
import { ContractorsForm, CONTRACTORS_FORM_ACTION_CREATE } from '@/components/contractors/ContractorsForm';
import FileUploader from '@/components/global/FileUploader';
import OneTimeContractProgressBar from '@/components/one-time-contract/OneTimeContractProgressBar';
import { SERVICE_CATEGORIES, COPYRIGHT_TYPES, CONTRACT_TEMPLATES, FORM_STORAGE_KEY, generateContractPreview } from '@/constants/oneTimeContract';
import { InfoIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { validateNoneCyrillic, GET_COUNTRY_NAME_BY_CODE, SUPPORTED_CURRENCIES, sanitazeInput } from '@/constants';
import { format } from 'date-fns';
import { getUser, createOneTimeContract, newContractor, uploadFiles, getContractors, calculateOneTimeContractAmounts } from '@/service/weexpertService';
import { Search } from '@/components/ui/combobox-search';
import { useQuery } from 'react-query';
import { toast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import CalculationBlock, { useContractCalculations } from './one-time-calculation-cards';

// Custom hook for contract calculations


const OneTimeContractPage = () => {
  const { t } = useTranslation(['translation', 'contracts']);
  const navigate = useNavigate();
  const [currentStage, setCurrentStage] = useState(1);
  const [inputChecked, setInputChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current user data
  const { data: authData } = useQuery({
    queryKey: 'userMe',
    queryFn: getUser,
    retry: false,
    refetchOnWindowFocus: false
  });

  // Extract and format CLIENT bank accounts from user data
  useEffect(() => {
    if (authData?.data?.bankAccounts) {
      const clientAccounts = authData.data.bankAccounts
        .filter(account => account.accountType === 'CLIENT')
        .map(account => ({
          value: account.id,
          label: `${account.account} (${account.currency})`,
          account: account.account,
          currency: account.currency,
          swift: account.swift
        }));
      setUserBankAccounts(clientAccounts);
    }
  }, [authData]);

  // Stage 1 - Counterparty Information
  const [selectedCounterparty, setSelectedCounterparty] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [counterpartyType, setCounterpartyType] = useState();
  const [counterpartyOrigin, setCounterpartyOrigin] = useState();
  const [counterpartyName, setCounterpartyName] = useState('');
  const [counterpartyAddress, setCounterpartyAddress] = useState('');
  const [counterpartyCity, setCounterpartyCity] = useState('');
  const [counterpartyEmail, setCounterpartyEmail] = useState('');
  const [counterpartyPostIndex, setCounterpartyPostIndex] = useState('');
  const [counterpartyCountry, setCounterpartyCountry] = useState();
  const [counterpartyNip, setCounterpartyNip] = useState('');

  // Stage 2 - Contract Details
  const [serviceCategory, setServiceCategory] = useState(null);
  const [serviceSubcategory, setServiceSubcategory] = useState(null);
  const [contractTitle, setContractTitle] = useState('');
  const [contractDescription, setContractDescription] = useState('');
  const [messageForCounterparty, setMessageForCounterparty] = useState('');
  const [messageToEmail, setMessageToEmail] = useState('');
  const [wantToSendMessage, setWantToSendMessage] = useState(false);
  const [copyrightType, setCopyrightType] = useState(null);
  const [priceNetto, setPriceNetto] = useState('');
  const [totalAmountToPay, setTotalAmountToPay] = useState('');
  const [clearHandMoney, setClearHandMoney] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState(null);
  const [currency, setCurrency] = useState('PLN');
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [userBankAccounts, setUserBankAccounts] = useState([]);

  // Input states for immediate feedback (separate from calculated values)
  const [nettoInput, setNettoInput] = useState('');
  const [bruttoInput, setBruttoInput] = useState('');
  const [clearHandInput, setClearHandInput] = useState('');

  // Timer reference for debouncing
  const debounceTimer = useRef(null);

  // Hint state for copyright type requirement
  const [showCopyrightHint, setShowCopyrightHint] = useState(false);

  // Stage 3 - Files
  const [contractFiles, setContractFiles] = useState([]);

  // Calculation state
  const [counterpartyCalculation, setCounterpartyCalculation] = useState(null);
  const [contractorCalculation, setContractorCalculation] = useState(null);

  // Get calculation data from API
  const {
    data: calculations,
    isLoading: calculationsLoading,
    error: calculationsError
  } = useContractCalculations(
    priceNetto,
    copyrightType?.value,
    selectedCounterparty?.id,
    typeof currency === 'object' ? currency?.value : currency || 'PLN'
  );

  // Load saved form data from localStorage
  useEffect(() => {
    /*
    const savedData = localStorage.getItem(FORM_STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setCurrentStage(parsedData.currentStage || 1);

        // Stage 1
        setSelectedCounterparty(parsedData.selectedCounterparty || null);
        setShowManualEntry(parsedData.showManualEntry || false);
        setCounterpartyType(parsedData.counterpartyType || null);
        setCounterpartyOrigin(parsedData.counterpartyOrigin || null);
        setCounterpartyName(parsedData.counterpartyName || '');
        setCounterpartyAddress(parsedData.counterpartyAddress || '');
        setCounterpartyCity(parsedData.counterpartyCity || '');
        setCounterpartyEmail(parsedData.counterpartyEmail || '');
        setCounterpartyPostIndex(parsedData.counterpartyPostIndex || '');
        setCounterpartyCountry(parsedData.counterpartyCountry || null);
        setCounterpartyNip(parsedData.counterpartyNip || '');

        // Stage 2
        setServiceCategory(parsedData.serviceCategory || null);
        setServiceSubcategory(parsedData.serviceSubcategory || null);
        setContractTitle(parsedData.contractTitle || '');
        setContractDescription(parsedData.contractDescription || '');
        setMessageForCounterparty(parsedData.messageForCounterparty || '');
        setMessageToEmail(parsedData.messageToEmail || '');
        setWantToSendMessage(parsedData.wantToSendMessage || false);
        setCopyrightType(parsedData.copyrightType || null);
        setPriceNetto(parsedData.priceNetto || '');
        setTotalAmountToPay(parsedData.totalAmountToPay || '');
        setClearHandMoney(parsedData.clearHandMoney || '');
        setPaymentDueDate(parsedData.paymentDueDate ? new Date(parsedData.paymentDueDate) : null);
        setCurrency(parsedData.currency || 'PLN');
        setSelectedBankAccountId(parsedData.selectedBankAccountId || '');

        // Sync input states with loaded data
        setNettoInput(parsedData.priceNetto || '');
        setBruttoInput(parsedData.totalAmountToPay || '');
        setClearHandInput(parsedData.clearHandMoney || '');

        // Stage 3
        setContractFiles(parsedData.contractFiles || []);

        setInputChecked(false);
      } catch (error) {
        console.error('Failed to load saved form data:', error);
      }
    }
    setIsLoading(false);
    */
  }, []);

  // Save form data to localStorage
  useEffect(() => {
    if (!isLoading) {
      const formData = {
        currentStage,
        // Stage 1
        selectedCounterparty,
        showManualEntry,
        counterpartyType,
        counterpartyOrigin,
        counterpartyName,
        counterpartyAddress,
        counterpartyCity,
        counterpartyEmail,
        counterpartyPostIndex,
        counterpartyCountry,
        counterpartyNip,
        // Stage 2
        serviceCategory,
        serviceSubcategory,
        contractTitle,
        contractDescription,
        messageForCounterparty,
        messageToEmail,
        wantToSendMessage,
        copyrightType,
        priceNetto,
        totalAmountToPay,
        clearHandMoney,
        paymentDueDate: paymentDueDate ? paymentDueDate.toISOString() : null,
        currency,
        selectedBankAccountId,
        // Stage 3
        contractFiles
      };
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    }
  }, [
    isLoading, currentStage,
    selectedCounterparty, showManualEntry,
    counterpartyType, counterpartyOrigin, counterpartyName, counterpartyAddress,
    counterpartyCity, counterpartyEmail, counterpartyPostIndex, counterpartyCountry, counterpartyNip,
    serviceCategory, serviceSubcategory, contractTitle, contractDescription,
    messageForCounterparty, messageToEmail, wantToSendMessage, copyrightType, priceNetto, totalAmountToPay, clearHandMoney, paymentDueDate, currency, selectedBankAccountId,
    contractFiles
  ]);

  const validateStage1 = () => {
    // If counterparty is selected from search, validation is simpler
    if (selectedCounterparty) {
      return true;
    }

    // If manual entry mode, validate all fields
    return (
      counterpartyType &&
      counterpartyOrigin &&
      counterpartyName && !counterpartyName.includes(',') && validateNoneCyrillic(counterpartyName) &&
      counterpartyAddress && validateNoneCyrillic(counterpartyAddress) &&
      counterpartyCity && validateNoneCyrillic(counterpartyCity) &&
      (!counterpartyPostIndex || validateNoneCyrillic(counterpartyPostIndex)) &&
      counterpartyCountry
    );
  };

  const validateStage2 = () => {
    return (
      serviceCategory &&
      serviceSubcategory &&
      contractTitle.trim().length > 0 &&
      contractDescription.trim().length > 0 &&
      copyrightType &&
      priceNetto && !isNaN(parseFloat(priceNetto)) &&
      paymentDueDate &&
      selectedBankAccountId
    );
  };

  const handleNext = async () => {
    setInputChecked(true);

    if (currentStage === 1 && validateStage1()) {
      // If manual entry is being used and no counterparty is selected yet, create it
      if (showManualEntry && !selectedCounterparty) {
        try {
          setIsSubmitting(true); // Show loading state

          // Create new counterparty
          const counterpartyResponse = await newContractor(
            counterpartyType?.value,
            [authData.data.id], // Link to current user/client
            counterpartyName,
            counterpartyAddress,
            counterpartyPostIndex,
            counterpartyCity,
            counterpartyEmail,
            counterpartyCountry?.content?.value || counterpartyCountry?.value,
            counterpartyNip,
            counterpartyOrigin?.value
          );

          if (counterpartyResponse.status === 200) {
            // Update selectedCounterparty with the newly created counterparty
            setSelectedCounterparty(counterpartyResponse.data);

            // Hide manual entry form and show display view
            setShowManualEntry(false);

            // Show success message
            toast({
              title: t('contracts:success'),
              description: t('contracts:counterparty_created_successfully'),
              variant: "success"
            });
          }

          setIsSubmitting(false);
        } catch (error) {
          setIsSubmitting(false);
          toast({
            title: t('contracts:error'),
            description: t('contracts:failed_to_create_counterparty'),
            variant: "destructive"
          });
          return; // Don't proceed to Stage 2 if creation failed
        }
      }

      // Proceed to Stage 2
      setCurrentStage(2);
      setInputChecked(false);
    } else if (currentStage === 2 && validateStage2()) {
      setCurrentStage(3);
      setInputChecked(false);
    }
  };

  const handlePrevious = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
      setInputChecked(false);
    }
  };

  const handleSave = async () => {
    if (!authData?.data) {
      toast({
        title: t('contracts:error'),
        description: t('contracts:user_data_not_available'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Upload files if any are selected
      let fileIds = [];
      if (contractFiles && contractFiles.length > 0) {
        const fileUploadResponse = await uploadFiles(contractFiles);
        if (fileUploadResponse.data.success) {
          fileIds = fileUploadResponse.data.files.map(file => file.id);
        } else {
          throw new Error(t('contracts:failed_to_upload_files'));
        }
      }

      // Step 2: Get counterparty ID (counterparty is already created in Stage 1)
      const counterpartyId = selectedCounterparty.id;

      // Step 3: Create one-time contract
      const contractData = {
        clientId: authData.data.id,
        counterpartyId: counterpartyId,
        serviceCategory: serviceCategory?.value,
        serviceSubcategory: serviceSubcategory?.value,
        title: contractTitle,
        description: contractDescription,
        messageForCounterparty: wantToSendMessage ? messageForCounterparty || undefined : undefined,
        counterpartyEmail: wantToSendMessage ? messageToEmail || undefined : undefined,
        copyrightType: copyrightType?.value,
        priceNetto: parseFloat(priceNetto),
        paymentDueDate: paymentDueDate ? format(paymentDueDate, 'yyyy-MM-dd') : null,
        currency: (() => {
          const contractCurrency = typeof currency === 'object' ? currency?.value : currency;
          return contractCurrency !== 'PLN' ? contractCurrency : undefined;
        })(),
        bankAccount: (() => {
          const selectedBankAccountValue = typeof selectedBankAccountId === 'object' ? selectedBankAccountId?.value : selectedBankAccountId;
          const selectedBankAccount = userBankAccounts.find(account => account.value === selectedBankAccountValue);
          return selectedBankAccount?.account || undefined;
        })(),
        fileIds: fileIds
      };

      const contractResponse = await createOneTimeContract(contractData);

      if (contractResponse.data.success) {
        // Clear form data from localStorage
        localStorage.removeItem(FORM_STORAGE_KEY);

        toast({
          title: t('contracts:success'),
          description: t('contracts:contract_created_successfully'),
        });

        // Navigate to one-time contracts list page
        navigate('/contracts/one-time');
      } else {
        throw new Error(contractResponse.data.message || t('contracts:failed_to_create_contract'));
      }

    } catch (error) {
      console.error('Error creating one-time contract:', error);
      toast({
        title: t('contracts:error'),
        description: error.response?.data?.message || error.message || t('contracts:failed_to_create_contract'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryOptions = () => {
    // Mapping from category IDs to translation keys
    const categoryKeyMap = {
      'education': 'education',
      'sport': 'sport',
      'programming': 'programming',
      'multimedia': 'multimedia',
      'texts': 'texts',
      'music': 'music',
      'other': 'other'
    };

    return Object.values(SERVICE_CATEGORIES).map(cat => ({
      value: cat.id,
      label: t(`contracts:${categoryKeyMap[cat.id]}`)
    }));
  };

  const getSubcategoryOptions = () => {
    if (!serviceCategory) return [];
    const category = Object.values(SERVICE_CATEGORIES).find(cat => cat.id === serviceCategory.value);
    return category ? category.subcategories.map(sub => ({
      value: sub.id,
      label: t(`contracts:${sub.id}`)
    })) : [];
  };

  // Mapping for copyright types to translation keys
  const copyrightKeyMap = {
    'COPYRIGHT': 'copyright_rights',
    'NO_TRANSFER': 'no_copyright_transfer'
  };
  const copyrightDescMap = {
    'COPYRIGHT': 'copyright_description',
    'NO_TRANSFER': 'no_transfer_description'
  };

  const getCopyrightOptions = () => {
    return Object.values(COPYRIGHT_TYPES).filter(type => type.id !== 'LICENSE').map(type => ({
      value: type.id,
      label: t(`contracts:${copyrightKeyMap[type.id]}`)
    }));
  };


  // Helper functions still needed for reverse calculations
  const getTaxDeductibleRate = (copyrightType) => {
    if (copyrightType === 'LICENSE' || copyrightType === 'COPYRIGHT') {
      return 0.5; // 50%
    }
    if (copyrightType === 'NO_TRANSFER') {
      return 0.2; // 20%
    }
    return 0;
  };

  // Reverse calculation: from brutto to netto
  const calculateNettoFromBrutto = (bruttoAmount, vatRate) => {
    if (!bruttoAmount || isNaN(parseFloat(bruttoAmount))) return null;
    const brutto = parseFloat(bruttoAmount);
    const netto = brutto / (1 + vatRate);
    return netto;
  };

  const debouncedCalculate = (field, value) => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      const origin = selectedCounterparty ? selectedCounterparty.origin : counterpartyOrigin?.value;
      const type = selectedCounterparty ? selectedCounterparty.counterpartyType : counterpartyType?.value;
      const vatRate = (origin === 'PL' || type === 'INDIVIDUAL') ? 0.23 : 0;

      if (field === 'netto') {
        // For netto input, just set the value - API calculations will handle the rest
        setPriceNetto(value);
        if (!value || isNaN(parseFloat(value))) {
          // Clear other fields if netto is invalid
          setTotalAmountToPay('');
          setClearHandMoney('');
          setBruttoInput('');
          setClearHandInput('');
        }
      } else if (field === 'brutto') {
        // Calculate netto from brutto and set it - API will handle rest
        if (value && !isNaN(parseFloat(value))) {
          const netto = calculateNettoFromBrutto(value, vatRate);
          if (netto) {
            const nettoValue = netto.toFixed(2);
            setPriceNetto(nettoValue);
            setNettoInput(nettoValue);
          } else {
            setPriceNetto('');
            setClearHandMoney('');
            setNettoInput('');
            setClearHandInput('');
          }
        } else {
          setPriceNetto('');
          setClearHandMoney('');
          setNettoInput('');
          setClearHandInput('');
        }
      } else if (field === 'clearHand') {
        // Calculate netto from clearHand and set it - API will handle rest
        if (value && !isNaN(parseFloat(value)) && copyrightType?.value) {
          const netto = calculateNettoFromClearHand(value, copyrightType?.value, vatRate);
          if (netto) {
            const nettoValue = netto.toFixed(2);
            setPriceNetto(nettoValue);
            setNettoInput(nettoValue);
          } else {
            setPriceNetto('');
            setTotalAmountToPay('');
            setNettoInput('');
            setBruttoInput('');
          }
        } else {
          setPriceNetto('');
          setTotalAmountToPay('');
          setNettoInput('');
          setBruttoInput('');
        }
      }
    }, 500); // 500ms debounce delay
  };

  // Handle amount field changes with immediate input feedback
  const handleAmountChange = (field, value) => {

    // Update input states immediately for responsive UI
    if (field === 'netto') {
      setNettoInput(sanitazeInput(value));
    } else if (field === 'brutto') {
      setBruttoInput(sanitazeInput(value));
    }
    // Trigger debounced calculation
    debouncedCalculate(field, sanitazeInput(value));
  };

  // Handle counterparty selection from search
  const handleCounterpartySelect = (selection) => {
    setSelectedCounterparty(selection.counterparty);
    setShowManualEntry(false);
    // Auto-populate fields for display purposes
    setCounterpartyName(selection.counterparty.name);
    setCounterpartyType({ value: selection.counterparty.counterpartyType });
    setCounterpartyOrigin({ value: selection.counterparty.origin });
    setCounterpartyAddress(selection.counterparty.address);
    setCounterpartyCity(selection.counterparty.city);
    setCounterpartyEmail(selection.counterparty.email || '');
    setCounterpartyPostIndex(selection.counterparty.postIndex);
    setCounterpartyCountry({ value: selection.counterparty.country });
    setCounterpartyNip(selection.counterparty.NIP || '');
  };

  // Handle switching to manual entry mode
  const handleAddNewCounterparty = () => {
    setSelectedCounterparty(null);
    setShowManualEntry(true);
    // Clear existing data
    setCounterpartyName('');
    setCounterpartyType(null);
    setCounterpartyOrigin(null);
    setCounterpartyAddress('');
    setCounterpartyCity('');
    setCounterpartyEmail('');
    setCounterpartyPostIndex('');
    setCounterpartyCountry(null);
    setCounterpartyNip('');
  };

  // Handle returning to search mode
  const handleBackToSearch = () => {
    setShowManualEntry(false);
    setSelectedCounterparty(null);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Hide copyright hint after 3 seconds
  useEffect(() => {
    if (showCopyrightHint) {
      const timer = setTimeout(() => {
        setShowCopyrightHint(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showCopyrightHint]);

  // Update calculations and form fields when API calculations change
  useEffect(() => {
    if (calculations) {
      // Set calculation state from API response
      setCounterpartyCalculation(calculations.counterparty);
      setContractorCalculation(calculations.contractor);

      // Update amount fields based on API calculations
      if (priceNetto && !isNaN(parseFloat(priceNetto))) {
        const bruttoValue = calculations.counterparty?.brutto?.toFixed(2) || '';
        const clearValue = calculations.contractor?.toPay?.toFixed(2) || '';

        setTotalAmountToPay(bruttoValue);
        setClearHandMoney(clearValue);

        // Also sync input states to prevent drift
        setNettoInput(priceNetto);
        setBruttoInput(bruttoValue);
        setClearHandInput(clearValue);
      }
    } else {
      // Clear calculations when no data available
      setCounterpartyCalculation(null);
      setContractorCalculation(null);
    }
  }, [calculations, priceNetto]);

  // Check if user has permission to create contracts (only CLIENT role)
  if (authData?.data?.roles !== 'CLIENT') {
    return (
      <div className="w-full px-8 py-10 bg-grayLightMainBg">
        <div className="text-4xl mb-8">{t('contracts:one_time_contract_title')}</div>
        <Card className="p-6 w-9/12">
          <div className="text-center">
            <h3 className="text-xl font-medium text-red-600 mb-4">{t('contracts:no_permissions')}</h3>
            <p className="text-gray-600 mb-6">
              {t('contracts:only_clients_can_create')}
            </p>
            <Button
              onClick={() => navigate('/contracts/one-time')}
              variant="outline"
            >
              {t('contracts:back_to_list')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full px-8 py-10 bg-grayLightMainBg">
      <div className="text-4xl mb-8">{t('contracts:one_time_contract_title')}</div>

      <OneTimeContractProgressBar currentStage={currentStage} />

      <div className="w-full lg:w-9/12 pt-8">
        {/* Stage 1: Counterparty Information */}
        {currentStage === 1 && (
          <div>
            <h2 className="text-2xl font-medium mb-6">{t('contracts:counterparty_data')}</h2>

            {/* Search Section */}
            {!showManualEntry && (
              <Card className="p-6 mb-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-3">{t('contracts:search_existing_counterparty')}</h3>
                  <Search
                    queryKey="counterparties"
                    fetchOption={(query) => {
                      if (!authData?.data?.id) return [];
                      return getContractors(100, 0, query, [{ field: 'client', value: [authData.data.id] }])
                        .then((res) => {
                          return res.data.counterparties
                            .map(counterparty => ({
                              label: `${counterparty.name}`,
                              value: counterparty.id,
                              counterparty
                            }));
                        });
                    }}
                    placeholder={t('contracts:type_counterparty_name')}
                    label={t('contracts:counterparty')}
                    value={selectedCounterparty ? selectedCounterparty.name : null}
                    onChange={handleCounterpartySelect}
                    error={inputChecked && !selectedCounterparty && !showManualEntry}
                    errorMessage={t('contracts:select_counterparty_or_add')}
                  />
                </div>

                {selectedCounterparty && (
                  <div className="mt-6">
                    <div className="w-full flex justify-between">
                      <div className="w-[49%]">
                        <InputWithAlert
                          label={t('contracts:counterparty_name')}
                          value={selectedCounterparty.name}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                        <InputWithAlert
                          label={t('contracts:origin')}
                          value={selectedCounterparty.origin === 'OTHER' ? t('contracts:rest_of_world') :
                            (selectedCounterparty.origin === 'EU' ? t('contracts:eu') : t('contracts:poland'))}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                        {selectedCounterparty.counterpartyType === 'COMPANY' && (
                          <InputWithAlert
                            label={t('contracts:nip')}
                            value={selectedCounterparty.NIP || ''}
                            onChange={() => {}}
                            disabled
                            inputClassName={'h-12 mt-2 mb-4 bg-white'}
                          />
                        )}
                        <InputWithAlert
                          label={t('contracts:email')}
                          value={selectedCounterparty.email || ''}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                      </div>
                      <div className="w-[49%]">
                        <InputWithAlert
                          label={t('contracts:type')}
                          value={selectedCounterparty.counterpartyType === 'COMPANY' ? t('contracts:company') : t('contracts:individual')}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                        <InputWithAlert
                          label={t('contracts:address')}
                          value={selectedCounterparty.address}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                        <InputWithAlert
                          label={t('contracts:postal_code')}
                          value={selectedCounterparty.postIndex}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                        <InputWithAlert
                          label={t('contracts:city')}
                          value={selectedCounterparty.city}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                        <InputWithAlert
                          label={t('contracts:country')}
                          value={GET_COUNTRY_NAME_BY_CODE(selectedCounterparty.country)}
                          onChange={() => {}}
                          disabled
                          inputClassName={'h-12 mt-2 mb-4 bg-white'}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleAddNewCounterparty}
                    className="flex items-center gap-2"
                  >
                    {t('contracts:add_new_counterparty')}
                  </Button>
                </div>
              </Card>
            )}

            {/* Manual Entry Section */}
            {showManualEntry && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium">{t('contracts:add_new_counterparty')}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBackToSearch}
                    className="flex items-center gap-2"
                  >
                    ← {t('contracts:back_to_search')}
                  </Button>
                </div>

                <ContractorsForm
                  action={CONTRACTORS_FORM_ACTION_CREATE}
                  inputChecked={inputChecked}
                  companyType={counterpartyType}
                  setCompanyType={setCounterpartyType}
                  NIP={counterpartyNip}
                  setNIP={setCounterpartyNip}
                  companyName={counterpartyName}
                  setCompanyName={setCounterpartyName}
                  companyOrigin={counterpartyOrigin}
                  setCompanyOrigin={setCounterpartyOrigin}
                  companyCountry={counterpartyCountry}
                  setCompanyCountry={setCounterpartyCountry}
                  postIndex={counterpartyPostIndex}
                  setPostIndex={setCounterpartyPostIndex}
                  mail={counterpartyEmail}
                  setMail={setCounterpartyEmail}
                  city={counterpartyCity}
                  setCity={setCounterpartyCity}
                  address={counterpartyAddress}
                  setAddress={setCounterpartyAddress}
                />
              </div>
            )}
          </div>
        )}

        {/* Stage 2: Contract Details */}
        {currentStage === 2 && (
          <div>
            <h2 className="text-2xl font-medium mb-6">{t('contracts:contract_details_title')}</h2>

            <div className="space-y-6">
              {/* Service Category */}
              <div className="flex gap-4">
                <div className="w-1/2">
                  <Combobox
                    label={t('contracts:service_category_label')}
                    value={serviceCategory}
                    placeholder={t('contracts:select_category')}
                    options={getCategoryOptions()}
                    onChange={(value) => {
                      setServiceCategory(value);
                      setServiceSubcategory(null);
                    }}
                    error={inputChecked && !serviceCategory}
                    errorMessage={t('contracts:field_required')}
                  />
                </div>
                <div className="w-1/2">
                  <Combobox
                    label={t('contracts:subcategory')}
                    value={serviceSubcategory}
                    placeholder={t('contracts:select_subcategory')}
                    options={getSubcategoryOptions()}
                    onChange={setServiceSubcategory}
                    disabled={!serviceCategory}
                    error={inputChecked && !serviceSubcategory}
                    errorMessage={t('contracts:field_required')}
                  />
                </div>
              </div>

              {/* Title and Description */}
              <div>
                <InputWithAlert
                  label={t('contracts:contract_title')}
                  value={contractTitle}
                  onChange={(e) => setContractTitle(e.target.value)}
                  placeholder={t('contracts:enter_contract_title')}
                  error={inputChecked && !contractTitle.trim()}
                  errorMessage={t('contracts:field_required')}
                />
                <div className="flex items-center gap-2 mt-1 text-xs text-blue-600">
                  <InfoIcon className="w-3 h-3" />
                  <span>{t('contracts:polish_language_note')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">{t('contracts:contract_description')}</label>
                <textarea
                  className={`w-full px-3 py-2 border rounded-md resize-none ${
                    inputChecked && !contractDescription.trim() ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows={4}
                  value={contractDescription}
                  onChange={(e) => setContractDescription(e.target.value)}
                  placeholder={t('contracts:describe_contract_details')}
                />
                {inputChecked && !contractDescription.trim() && (
                  <p className="text-red-500 text-sm mt-1">{t('contracts:field_required')}</p>
                )}
                <div className="flex items-center gap-2 mt-1 text-xs text-blue-600">
                  <InfoIcon className="w-3 h-3" />
                  <span>{t('contracts:polish_language_note')}</span>
                </div>
              </div>

              {/* Contract Preview */}
              {serviceCategory && serviceSubcategory && (
                <Card className="p-4 bg-gray-50 border-l-4 border-blue-500">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{t('contracts:contract_preview')}</h4>
                  <p className="text-sm text-gray-800 leading-relaxed font-mono">
                    {generateContractPreview(
                      serviceCategory?.value,
                      serviceSubcategory?.value,
                      contractTitle,
                      contractDescription
                    )}
                  </p>
                </Card>
              )}

              {/* Message for Counterparty */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="wantToSendMessage"
                    checked={wantToSendMessage}
                    onChange={(e) => {
                      setWantToSendMessage(e.target.checked);
                      if (!e.target.checked) {
                        setMessageForCounterparty('');
                        setMessageToEmail('');
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="wantToSendMessage" className="text-sm font-medium text-gray-700">
                    {t('contracts:send_message_to_counterparty_checkbox')}
                  </label>
                </div>

                {wantToSendMessage && (
                  <div className="space-y-4 pl-6 border-l-2 border-gray-200">
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('contracts:counterparty_email_input_label')}</label>
                      <input
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={messageToEmail}
                        onChange={(e) => setMessageToEmail(e.target.value)}
                        placeholder={t('contracts:counterparty_email_placeholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('contracts:message_for_counterparty_input_label')}</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        value={messageForCounterparty}
                        onChange={(e) => setMessageForCounterparty(e.target.value)}
                        placeholder={t('contracts:additional_message_placeholder')}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Copyright Type */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('contracts:copyright_transfer_type')}</label>
                <div className="space-y-3">
                  {Object.values(COPYRIGHT_TYPES).filter(type => type.id !== 'LICENSE').map((type) => (
                    <Card
                      key={type.id}
                      className={`p-4 cursor-pointer border-2 transition-colors ${
                        copyrightType?.value === type.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                      onClick={() => {
                        setCopyrightType({ value: type.id, label: t(`contracts:${copyrightKeyMap[type.id]}`) });
                        // Clear all amount fields when copyright type changes
                        if (copyrightType?.value !== type.id) {
                          setPriceNetto('');
                          setTotalAmountToPay('');
                          setClearHandMoney('');
                          setNettoInput('');
                          setBruttoInput('');
                          setClearHandInput('');
                          setCounterpartyCalculation(null);
                          setContractorCalculation(null);
                        }
                      }}
                    >
                      <div className="flex items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{t(`contracts:${copyrightKeyMap[type.id]}`)}</h4>
                          <p className="text-sm text-gray-600 mt-1">{t(`contracts:${copyrightDescMap[type.id]}`)}</p>
                        </div>
                        <InfoIcon className="w-5 h-5 text-gray-400 ml-2" />
                      </div>
                    </Card>
                  ))}
                </div>
                {inputChecked && !copyrightType && (
                  <p className="text-red-500 text-sm mt-1">{t('contracts:select_copyright_type')}</p>
                )}
              </div>

              {/* Currency and Bank Account */}
              <div className="flex gap-4">
                <div className="w-1/2">
                  <Combobox
                    label={t('invoices:currency')}
                    value={currency}
                    placeholder={t('signup:select_currency')}
                    options={SUPPORTED_CURRENCIES}
                    onChange={setCurrency}
                  />
                </div>
                <div className="w-1/2">
                  <Combobox
                    label={t('signup:bank_account')}
                    value={selectedBankAccountId}
                    placeholder={t('signup:select_bank_account')}
                    options={userBankAccounts}
                    onChange={setSelectedBankAccountId}
                    error={inputChecked && !selectedBankAccountId}
                    errorMessage={t('signup:empty_data_invalid')}
                  />
                  {userBankAccounts.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      {t('no_client_bank_accounts_available')}
                    </p>
                  )}
                </div>
              </div>

              {/* Currency Mismatch Warning */}
              {(() => {
                const selectedBankAccountValue = typeof selectedBankAccountId === 'object' ? selectedBankAccountId?.value : selectedBankAccountId;
                const selectedBankAccount = userBankAccounts.find(account => account.value === selectedBankAccountValue);
                const bankAccountCurrency = selectedBankAccount?.currency;
                const contractCurrency = typeof currency === 'object' ? currency?.value : currency;
                const showWarning = contractCurrency && bankAccountCurrency && contractCurrency !== bankAccountCurrency;
                if (showWarning) {
                  return (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            {t('currency_mismatch_warning_title')}
                          </h3>
                          <div className="mt-1 text-sm text-yellow-700">
                            <p>
                              {t('currency_mismatch_warning_message', {
                                contractCurrency: contractCurrency,
                                bankCurrency: bankAccountCurrency
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Price Fields */}
              {(() => {
                const selectedCurrency = typeof currency === 'object' ? currency?.value : currency || 'PLN';

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <InputWithAlert
                        label={`${t('contracts:client_will_pay')} (${selectedCurrency})`}
                        value={bruttoInput}
                        onChange={(e) => handleAmountChange('brutto', e.target.value)}
                        placeholder="0.00"
                        disabled={!copyrightType}
                        onFocus={() => !copyrightType && setShowCopyrightHint(true)}
                        inputClassName={`h-12 mt-1 ${!copyrightType ? 'cursor-not-allowed opacity-60' : ''}`}
                      />
                    </div>
                    <div>
                      <InputWithAlert
                        label={`${t('contracts:client_will_pay_net')} (${selectedCurrency})`}
                        value={nettoInput}
                        onChange={(e) => handleAmountChange('netto', e.target.value)}
                        placeholder="0.00"
                        error={inputChecked && (!priceNetto || isNaN(parseFloat(priceNetto)))}
                        errorMessage={t('contracts:enter_valid_amount')}
                        disabled={!copyrightType}
                        onFocus={() => !copyrightType && setShowCopyrightHint(true)}
                        inputClassName={`h-12 mt-1 ${!copyrightType ? 'cursor-not-allowed opacity-60' : ''}`}
                      />
                    </div>
                    <div>
                      <InputWithAlert
                        label={`${t('contracts:you_will_earn')} (${selectedCurrency})`}
                        value={clearHandInput}
                        disabled={true}
                        onChange={(e) => handleAmountChange('clearHand', e.target.value)}
                        placeholder="0.00"
                        onFocus={() => !copyrightType && setShowCopyrightHint(true)}
                        inputClassName={`h-12 mt-1 ${!copyrightType ? 'cursor-not-allowed opacity-60' : ''}`}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Copyright Type Hint */}
              {showCopyrightHint && !copyrightType && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2">
                  <InfoIcon className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">
                    {t('contracts:select_copyright_type_first')}
                  </p>
                </div>
              )}

              {/* Payment Date */}
              <div className="w-full md:w-[49%]">
                <DatePicker
                  date={paymentDueDate}
                  setDate={setPaymentDueDate}
                  disabled={{ before: new Date() } }
                  label={t('contracts:payment_due_date')}
                  error={inputChecked && !paymentDueDate}
                  errorMessage={t('contracts:select_payment_date')}
                  inputClassName={'h-12 mb-2'}
                />
              </div>

              {/* Calculation Blocks */}
              <CalculationBlock
                priceNetto={parseFloat(priceNetto) || 0}
                currency={currency}
                copyrightType={copyrightType}
                selectedCounterparty={selectedCounterparty}
                counterpartyOrigin={counterpartyOrigin}
                counterpartyType={counterpartyType}
                t={t}
              />
            </div>
          </div>
        )}

        {/* Stage 3: Files */}
        {currentStage === 3 && (
          <div>
            <h2 className="text-2xl font-medium mb-6">{t('contracts:files')}</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">{t('contracts:contract_files')}</label>
                <FileUploader onChange={setContractFiles} />
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStage === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('contracts:back')}
          </Button>

          <div className="flex gap-3">
            {currentStage < 3 && (
              <Button
                onClick={handleNext}
                className="flex items-center gap-2"
              >
                {t('contracts:next')}
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}

            {currentStage === 3 && (
              <Button
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-8"
              >
                {isSubmitting ?
                  (contractFiles.length > 0 ? t('contracts:uploading_files_saving') : t('contracts:saving'))
                  : t('contracts:save')
                }
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneTimeContractPage;
